import type {
	LabChartConfig,
	LabChartTypeEnum,
	LabParametrizedConcept,
	LabViewConfig,
} from "@eu.mdotm.api/portfolio-lab";
import type {
	ConfidenceLevelEnum,
	ParameterizedConceptDTO,
	ParameterizedConceptDTORelativeness,
	ParameterizedConceptDTORepresentationModeEnum,
	SourceEnum,
	TimeHorizonEnum,
} from "@eu.mdotm.api/smart-workspaces";
import type { PortfolioLabChartItem, PortfolioLabProps } from "../context";
import {
	DEFAULT_LAB_CHARTS,
	defaultChartName,
	parseChartPreferences,
	toChartItem,
	toWirePreference,
} from "./chart-preferences";
import type { PortfolioLabAdapter } from "./contract";
import { parseGridPreferences, toWireGridPreferences } from "./grid-preferences";
import { layoutToPageSplit, pageSplitToLayout } from "./page-split";

/**
 * The persisted {@link LabParametrizedConcept} is a strict subset of the rich {@link ParameterizedConceptDTO} the
 * metrics data query consumes — it has no `source`/`relativeness`. We tuck those into the opaque `parameters` bag
 * (backend persists `parameters` as-is) under reserved keys so the round-trip is lossless.
 */
const LAB_PC_SOURCE_KEY = "__labSource";
const LAB_PC_RELATIVENESS_KEY = "__labRelativeness";

function toLabParametrizedConcept(pc: ParameterizedConceptDTO): LabParametrizedConcept {
	const { parameters, source, relativeness, representationMode, ...rest } = pc;
	return {
		uuid: pc.uuid ?? crypto.randomUUID(),
		conceptId: pc.conceptId,
		label: pc.label,
		representationMode,
		timeHorizon: rest.timeHorizon,
		confidenceLevel: rest.confidenceLevel,
		parameters: {
			...(parameters ?? {}),
			...(source != null ? { [LAB_PC_SOURCE_KEY]: source as unknown as object } : {}),
			...(relativeness != null ? { [LAB_PC_RELATIVENESS_KEY]: relativeness as unknown as object } : {}),
		},
	};
}

function fromLabParametrizedConcept(c: LabParametrizedConcept): ParameterizedConceptDTO {
	const { [LAB_PC_SOURCE_KEY]: source, [LAB_PC_RELATIVENESS_KEY]: relativeness, ...parameters } = c.parameters ?? {};
	return {
		uuid: c.uuid,
		conceptId: c.conceptId,
		label: c.label,
		representationMode: c.representationMode as ParameterizedConceptDTORepresentationModeEnum | undefined,
		timeHorizon: c.timeHorizon as TimeHorizonEnum | undefined,
		confidenceLevel: c.confidenceLevel as ConfidenceLevelEnum | undefined,
		source: source as unknown as SourceEnum | undefined,
		relativeness: relativeness as unknown as ParameterizedConceptDTORelativeness | undefined,
		parameters,
	};
}

/** Maps the full lab view config (charts, layout/pageSplit, decimals) between the API and context state. */
export const labSessionAdapter = {
	toContext(config: LabViewConfig | null | undefined): PortfolioLabProps {
		const charts = config?.charts;
		const mapped: PortfolioLabChartItem[] =
			charts && charts.length > 0
				? charts.map((c) => {
						const parsedPreferences = parseChartPreferences(c.preferences);
						return toChartItem({
							uuid: c.uuid,
							name: c.label,
							visible: parsedPreferences.visible,
							type: c.chartType,
							preference: parsedPreferences.preference,
							series: c.series,
						});
					})
				: [...DEFAULT_LAB_CHARTS];

		// Ensure all three chart types are always present
		const presentTypes = new Set(mapped.map((c) => c.type));
		const allTypes: LabChartTypeEnum[] = ["PERFORMANCE", "EXPOSURE", "TABLE"];
		for (const type of allTypes) {
			if (!presentTypes.has(type)) {
				mapped.push(toChartItem({ uuid: crypto.randomUUID(), name: defaultChartName(type), visible: true, type }));
			}
		}

		return {
			decimals: config?.decimals ?? 2,
			pageSplit: layoutToPageSplit(config?.layout),
			charts: mapped,
			investments: [],
			parametrizedConcepts: (config?.parameterizedConcepts ?? []).map(fromLabParametrizedConcept),
			grid: { ...parseGridPreferences(config?.grid?.preferences), projection: config?.grid?.projection },
		};
	},
	toUpstream(lab: PortfolioLabProps): LabViewConfig {
		return {
			decimals: lab.decimals,
			layout: pageSplitToLayout(lab.pageSplit),
			parameterizedConcepts: lab.parametrizedConcepts.map(toLabParametrizedConcept),
			charts: lab.charts.map(
				(c): LabChartConfig => ({
					uuid: c.uuid,
					chartType: c.type,
					label: c.name,
					// `series` is the data dimension (which concepts), persisted independently of the view-only
					// `preference`. Charts without series (PERFORMANCE/EXPOSURE) send `undefined`.
					series: c.series.length > 0 ? c.series : undefined,
					preferences: { version: 1, data: { visible: c.visible, preference: toWirePreference(c) } },
				}),
			),
			// `projection` (concept columns inside the tree) is not FE-driven yet, but updateLabSession is a
			// full-replace PUT — so echo it back verbatim rather than dropping it (which would wipe a value
			// the FE didn't author). The FE-owned state lives in the free-form `preferences` bag. See
			// grid-preferences.ts.
			grid: { projection: lab.grid.projection, preferences: toWireGridPreferences(lab.grid) },
		};
	},
} satisfies PortfolioLabAdapter;
